import { Outlet } from 'react-router-dom'

import { Box, Button, Tag, TagColor } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useProjectSpendingDataTable } from 'generated/resources/datatable.graphql'

import { observer } from 'mobx-react'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/expenses/components/expenses/DataTable/_Amount'
import { Category } from 'modules/expenses/components/expenses/DataTable/_Category'
import { Date as EffectiveOn } from 'modules/expenses/components/expenses/DataTable/_Date'
import { Owner } from 'modules/expenses/components/expenses/DataTable/_Owner'
import { Status } from 'modules/expenses/components/expenses/DataTable/_Status'
import { IProjectSpendingPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage/types'

import { UpsellProjectSpendingMessage } from '../../upsells/UpsellProjectSpendingMessage'
import NoSpendingMessage from './TableBody/NoSpendingMessage'

interface IProjectSpendingDataTableProps {
  hasCost: boolean
  hasExpenses: boolean
  openSidePanel: (newData: IProjectSpendingPanelData) => void
  projectId: number
  projectCurrency: string
  title?: string
}

const ProjectSpendingDataTable = ({
  hasCost,
  hasExpenses,
  openSidePanel,
  projectId,
  projectCurrency,
  title,
}: IProjectSpendingDataTableProps) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const source = useProjectSpendingDataTable(
    [
      {
        kind: ProjectionKind.Custom,
        identifier: 'owner',
        template: 'owner',
        title: i18n.t('project_management.spending.table.headers.employee'),
        render: ({ expensable_owner }) => {
          return (
            <Box minWidth="s200">
              <Owner owner={expensable_owner} />
            </Box>
          )
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'effectiveOn',
        template: 'effectiveOn',
        title: i18n.t('project_management.spending.table.headers.document_date'),
        render: ({ effectiveOn }) => {
          return (
            <Box minWidth="s140">
              <EffectiveOn date={effectiveOn} />
            </Box>
          )
        },
        sortBy: 'effectiveOn',
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'amount',
        template: '{{legalEntityAmountCurrency}} {{ legalEntityAmountCents | divided_by: 100}}',
        title: i18n.t('project_management.spending.table.headers.amount'),
        render: ({
          legalEntityAmountCents,
          legalEntityAmountCurrency,
          originalAmountCents,
          originalAmountCurrency,
        }) => {
          return (
            <Box minWidth="s120">
              <Amount
                amount={Number(legalEntityAmountCents) || originalAmountCents}
                currency={legalEntityAmountCurrency || originalAmountCurrency || ''}
              />
            </Box>
          )
        },
        sortBy: 'legalEntityAmountCents',
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'subproject',
        template: 'subproject',
        title: i18n.t('project_management.spending.table.headers.subproject'),
        render: ({ subproject_name }) => {
          if (!subproject_name) return null
          return (
            <Box flexDirection="row" alignItems="center">
              <Tag type={TagColor.neutral}>{subproject_name}</Tag>
            </Box>
          )
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'category',
        template: 'category',
        title: i18n.t('project_management.spending.table.headers.category'),
        render: ({ expensable_expense: expense, expensable_mileage: mileage }) => {
          return <Category expensable={expense || mileage} />
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'status',
        template: 'status',
        title: i18n.t('project_management.spending.table.headers.status'),
        render: ({ expensable_status }) => {
          return (
            expensable_status && (
              <Box width="s80">
                <Status status={expensable_status} />
              </Box>
            )
          )
        },
        sortBy: 'status',
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        render: ({
          effectiveOn,
          exchangeRate,
          expensable_id,
          expensable_owner,
          legalEntityAmountCents,
          legalEntityAmountCurrency,
          originalAmountCents,
          originalAmountCurrency,
        }) => {
          if (!expensable_id || !expensable_owner) return null
          return (
            <Button
              onClick={() => {
                openSidePanel({
                  effectiveOn,
                  employeeName: expensable_owner.access.fullName,
                  exchangeRate,
                  expensableId: expensable_id,
                  legalEntityLegalName: expensable_owner.legalEntity?.legalName,
                  legalEntityAmountCents: Number(legalEntityAmountCents),
                  legalEntityAmountCurrency: legalEntityAmountCurrency,
                  originalAmountCents: originalAmountCents || 0,
                  originalAmountCurrency: originalAmountCurrency || projectCurrency,
                })
              }}
              type="secondary"
              iconName="arrow-right"
            />
          )
        },
      },
    ],
    { variables: { projectIds: [projectId], employeeUserNameLike: debouncedSearch } }
  )

  return (
    <Box paddingTop="s24">
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'cursor',
          itemsPerPage: !hasCost || !hasExpenses ? 0 : 25,
          infiniteScroll: true,
        }}
        sort={{ path: 'legalEntityAmountCents' }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            search={{
              placeholder: i18n.t('common.search_by'),
              value: search,
              onChange: onSearch,
            }}
            title={title}
          />
          <DataTable.Table
            autoWidth
            emptyState={
              !hasCost || !hasExpenses ? (
                <UpsellProjectSpendingMessage hasCost={hasCost} hasExpenses={hasExpenses} />
              ) : (
                <NoSpendingMessage />
              )
            }
          />
        </Box>
        <Outlet />
      </DataTable.Provider>
    </Box>
  )
}

export default observer(ProjectSpendingDataTable)
